import { RequestMethod, pepApiRequest } from 'src/services';

export interface AppVersion {
  id: string;
  name: string;
}

export interface StreamingApp {
  id: string;
  title: string;
  icon: string;
  productArea: string;
  category?: string;
  latestVersion: AppVersion;
  versions: AppVersion[];
  description?: string;
  status?: 'active' | 'inactive' | 'maintenance';
  lastUpdated?: string;
  mediaServer?: string;
  mediaPort?: number;
}

const API_APPS = '/apps';
const API_APP = '/apps/:appId';

// Mock data for testing
let mockApps: StreamingApp[] = [
  {
    id: '1',
    title: 'Production Line 1',
    icon: '/pep-logo.png',
    productArea: 'Productone',
    category: 'Manufacturing',
    latestVersion: { id: '1.2', name: 'v1.2' },
    versions: [
      { id: '1.0', name: 'v1.0' },
      { id: '1.1', name: 'v1.1' },
      { id: '1.2', name: 'v1.2' },
    ],
    description: 'Main production line monitoring application',
    status: 'active',
    lastUpdated: '2025-09-15',
  },
  {
    id: '2',
    title: 'Production Line 2',
    icon: '/pep-logo.png',
    productArea: 'Producttwo',
    category: 'Manufacturing',
    latestVersion: { id: '2.0', name: 'v2.0' },
    versions: [
      { id: '1.0', name: 'v1.0' },
      { id: '2.0', name: 'v2.0' },
    ],
    description: 'Secondary production line monitoring',
    status: 'active',
    lastUpdated: '2025-09-10',
  },
  {
    id: '3',
    title: 'Quality Control',
    icon: '/pep-logo.png',
    productArea: 'QA',
    category: 'Quality',
    latestVersion: { id: '3.1', name: 'v3.1' },
    versions: [
      { id: '3.0', name: 'v3.0' },
      { id: '3.1', name: 'v3.1' },
    ],
    description: 'Quality assurance monitoring system',
    status: 'maintenance',
    lastUpdated: '2025-09-05',
  },
];

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });

const USE_MOCK = true;

export const fetchApps = async (): Promise<StreamingApp[]> => {
  if (USE_MOCK) {
    await delay(500);
    return [...mockApps];
  }

  return pepApiRequest({
    method: RequestMethod.Get,
    url: API_APPS,
  });
};

export const fetchAppById = async (appId: string): Promise<StreamingApp> => {
  if (USE_MOCK) {
    await delay(300);
    const app = mockApps.find((a) => a.id === appId);
    if (!app) {
      throw new Error(`App with id ${appId} not found`);
    }
    return { ...app };
  }

  return pepApiRequest({
    method: RequestMethod.Get,
    url: API_APP.replace(':appId', appId),
  });
};

export const createApp = async (
  appData: Omit<StreamingApp, 'id'>
): Promise<StreamingApp> => {
  if (USE_MOCK) {
    await delay(800);
    const newApp: StreamingApp = {
      ...appData,
      id: String(Date.now()),
    };
    mockApps = [...mockApps, newApp];
    return { ...newApp };
  }

  return pepApiRequest({
    method: RequestMethod.Post,
    url: API_APPS,
    params: appData,
  });
};

export const updateApp = async (
  appId: string,
  appData: Partial<StreamingApp>
): Promise<StreamingApp> => {
  if (USE_MOCK) {
    await delay(600);
    const appIndex = mockApps.findIndex((a) => a.id === appId);
    if (appIndex === -1) {
      throw new Error(`App with id ${appId} not found`);
    }

    const updatedApp = {
      ...mockApps[appIndex],
      ...appData,

      versions: appData.versions || mockApps[appIndex].versions,
    };

    mockApps = [
      ...mockApps.slice(0, appIndex),
      updatedApp,
      ...mockApps.slice(appIndex + 1),
    ];

    return { ...updatedApp };
  }

  return pepApiRequest({
    method: RequestMethod.Put,
    url: API_APP.replace(':appId', appId),
    params: appData,
  });
};

export const deleteApp = async (appId: string): Promise<void> => {
  if (USE_MOCK) {
    await delay(400);
    const appIndex = mockApps.findIndex((a) => a.id === appId);
    if (appIndex === -1) {
      throw new Error(`App with id ${appId} not found`);
    }

    mockApps = [
      ...mockApps.slice(0, appIndex),
      ...mockApps.slice(appIndex + 1),
    ];

    return undefined;
  }

  return pepApiRequest({
    method: RequestMethod.Delete,
    url: API_APP.replace(':appId', appId),
  });
};

export const addAppVersion = async (
  appId: string,
  versionData: AppVersion
): Promise<AppVersion> => {
  if (USE_MOCK) {
    await delay(300);
    const appIndex = mockApps.findIndex((a) => a.id === appId);
    if (appIndex === -1) {
      throw new Error(`App with id ${appId} not found`);
    }

    const updatedApp = {
      ...mockApps[appIndex],
      versions: [...mockApps[appIndex].versions, versionData],
    };

    mockApps = [
      ...mockApps.slice(0, appIndex),
      updatedApp,
      ...mockApps.slice(appIndex + 1),
    ];

    return versionData;
  }

  return pepApiRequest({
    method: RequestMethod.Post,
    url: `${API_APP.replace(':appId', appId)}/versions`,
    params: versionData,
  });
};
